import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  updateTokens,
  clearTokens,
} from '../src/pages/login/cookies';

let baseURL = 'http://localhost:4000'

if (process.env.NODE_ENV === 'production') {
  baseURL = 'https://server.bcp-pitstop.com'
}

const instance = axios.create({
  baseURL,
  timeout: 100000,
  headers: {
    'Content-type': 'application/json',
  }
});

/*
  JWT authentication - see JWT_AUTH_PLAN.md and JWT_CLIENT_INTEGRATION.md in
  coms_bcp_backend.

  The backend runs this in three modes. While it is 'off' or 'optional' a client
  with no token still works, so this can ship ahead of the cutover; once it is
  'enforce', anything not sending a verifiable token stops working.

  Two status codes carry the whole contract, and they are not interchangeable:

    403  the ACCESS token is missing, expired or invalid  -> refresh, retry once
    401  from /renewAccessToken only, the session is over -> log out

  Reading 401 where 403 is meant logs people out instead of refreshing them;
  reading 403 where 401 is meant leaves the app retrying a refresh that can never
  succeed. Note the previous (commented-out) version of this file keyed off
  `error.response.data.message === 'token_expired'`, which the backend does not
  send - it would never have refreshed anything.

  A 500 is a broken server, not an expired session, and must NOT log anybody out.
*/

const AUTH_PATHS = ['/renewAccessToken', '/profile/login', '/profile/logout', '/profile/sendMail'];

const isAuthPath = (url) => AUTH_PATHS.some((p) => String(url || '').includes(p));

instance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    // Only send the header when there is something to send. Interpolating
    // unconditionally produces the literal string "Bearer undefined", which the
    // backend has to special-case.
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    } else {
      delete config.headers['Authorization'];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

const sessionExpired = () => {
  const error = new Error('Session expired');
  error.response = { status: 401 };
  return error;
};

/*
  Single-flight refresh.

  Refresh tokens rotate server-side: spending one invalidates it. A dashboard
  fires several requests as it loads, so when the access token expires they all
  403 at once. Without this guard each would start its own refresh, the first
  would rotate the token and the rest would present one that had just been
  revoked. The backend absorbs that for 60 seconds by handing back the same
  replacement, but past that window it reads repeated use as a stolen token and
  kills the whole session. One shared request avoids the question entirely.
*/
let refreshPromise = null;

function refreshTokens() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.reject(sessionExpired());

  // Deliberately a bare axios call: routing it through `instance` would put it
  // through this same interceptor while a refresh is already in flight.
  refreshPromise = axios
    .post(baseURL + '/renewAccessToken', { refreshToken })
    .then((res) => {
      const accessToken = res && res.data && res.data.accessToken;
      const rotated = res && res.data && res.data.refreshToken;

      if (!accessToken || !rotated) throw sessionExpired();

      updateTokens(accessToken, rotated);
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

const endSession = () => {
  clearTokens();
  localStorage.clear();
  sessionStorage.clear();

  // Full reload rather than a route change: it clears redux, any in-flight
  // requests and every screen holding data the user can no longer fetch.
  if (typeof window !== 'undefined' && !String(window.location.pathname).includes('/login')) {
    window.location.replace(window.location.origin + '/login');
  }
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response && error.response.status;

    if (status === 403 && config && !config._retry && !isAuthPath(config.url)) {
      config._retry = true;

      try {
        const accessToken = await refreshTokens();
        config.headers = { ...config.headers, Authorization: 'Bearer ' + accessToken };
        return instance(config);
      } catch (e) {
        // Only a 401 means the session is genuinely over. A 500 or a dropped
        // connection must leave the user logged in.
        if (e && e.response && e.response.status === 401) endSession();
        return Promise.reject(e);
      }
    }

    if (status === 401) endSession();

    return Promise.reject(error)
  }
);

/*
  Ends the session properly: tells the backend to revoke the refresh token before
  clearing local state, so a token left in this browser cannot be used again.

  Fire-and-forget, and on bare axios - the authenticated instance would try to
  refresh the very token being thrown away. Logging out must never fail because
  the network is down; the backend returns 200 even for a token it has never seen.
*/
export const logout = () => {
  const refreshToken = getRefreshToken();

  if (refreshToken) {
    axios.post(baseURL + '/profile/logout', { refreshToken }).catch(() => { });
  }

  clearTokens();
};

export const base_url = baseURL;
export default instance;
